import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { tenantsApi } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { formatDate } from '@/lib/utils';
import { Search, Users, Globe } from 'lucide-react';
import { usePermissions } from '@/hooks/use-permissions';
import { AddTenantDialog } from '@/components/add-tenant-dialog';
import { QueryState } from '@/components/query-state';

function TenantsSkeleton() {
  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {[1, 2, 3].map((i) => (
        <Card key={i} className="animate-pulse">
          <CardHeader>
            <div className="h-6 w-32 bg-muted rounded" />
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="h-4 w-48 bg-muted rounded" />
              <div className="h-4 w-32 bg-muted rounded" />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export default function TenantsPage() {
  const [search, setSearch] = useState('');
  const permissions = usePermissions();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['tenants', search],
    queryFn: () => tenantsApi.list({ query: search || undefined }),
  });

  const tenants = data?.data || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <span className="rf3-eyebrow">Administration</span>
          <h2 className="text-3xl font-bold tracking-tight text-primary">Tenants</h2>
          <p className="text-muted-foreground">
            Manage organization tenants and their domain mappings
          </p>
        </div>
        {permissions.canManageTenants && <AddTenantDialog />}
      </div>

      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search tenants..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      <QueryState
        isLoading={isLoading}
        isError={isError}
        error={error}
        isEmpty={tenants.length === 0}
        skeleton={<TenantsSkeleton />}
        emptyMessage="No tenants found"
      >
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {tenants.map((tenant) => (
            <Card key={tenant.id} className="hover:border-primary/50 transition-colors">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">{tenant.name}</CardTitle>
                  <span
                    className={`px-2 py-1 text-xs rounded-full ${
                      tenant.status === 'active'
                        ? 'bg-green-100 text-green-800'
                        : tenant.status === 'suspended'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}
                  >
                    {tenant.status}
                  </span>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Globe className="h-4 w-4" />
                  <span>{tenant.slug}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Users className="h-4 w-4" />
                  <span>{tenant.userCount || 0} users</span>
                </div>
                <div className="text-xs text-muted-foreground">
                  Created {formatDate(tenant.createdAt)}
                </div>
                {tenant.mappings.length > 0 && (
                  <div className="pt-2 border-t">
                    <p className="text-xs font-medium mb-1">Email Domains</p>
                    <div className="flex flex-wrap gap-1">
                      {tenant.mappings.map((m) => (
                        <span
                          key={m.id}
                          className={`px-2 py-0.5 text-xs rounded ${
                            m.isPrimary ? 'bg-primary/10 text-primary' : 'bg-muted'
                          }`}
                        >
                          {m.emailDomain}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </QueryState>
    </div>
  );
}
